<template>
  <b-server-table
    :filter-props="filterProps"
    :headers="auditRecommendationTaskListHeaders"
    :request-func="
      query =>
        taskList({
          ...query,
          typeId: 5,
        })
    "
    @click:row="openTask">
    <template #action-button>
      <v-tooltip :text="recommendationTooltipText" location="bottom">
        <template #activator="{ props }">
          <span v-bind="props">
            <v-btn
              border
              color="white"
              density="comfortable"
              icon="mdi-information-outline"
              readonly></v-btn>
          </span>
        </template>
      </v-tooltip>
    </template>

    <template #item.audit="{ item }">
      <div class="d-flex flex-column">
        <span class="font-weight-bold">
          {{ item.entity?.flaw?.audit?.name || '---' }}
        </span>
        <v-chip
          :style="getColorByStatus(item.entity?.flaw?.audit?.statusId)"
          border
          class="font-weight-bold"
          density="comfortable"
          rounded="lg"
          size="small"
          variant="flat"
          style="width: max-content">
          {{ item.entity?.flaw?.audit?.status?.name || '---' }}
        </v-chip>
      </div>
    </template>

    <template #item.task="{ item }">
      <div class="d-flex flex-column">
        <div
          class="font-weight-medium"
          :class="{
            'text-medium-emphasis text-decoration-line-through': isTaskDone(
              item.statusId
            ),
          }">
          {{ item.topic || '---' }}
        </div>
        <div class="font-size-12 text-medium-emphasis">
          {{ item?.description || '---' }}
        </div>
      </div>
    </template>

    <template #item.executor="{ item }">
      <span class="font-weight-medium">{{
        getPersonName(item.executor, { initials: true }) || '---'
      }}</span>
    </template>

    <template #item.author="{ item }">
      <span class="font-weight-medium">{{
        getPersonName(item.author, { initials: true }) || '---'
      }}</span>
    </template>

    <template #item.created_at="{ item }">
      <span v-if="item.created_at">
        {{
          new CustomDate(item.created_at).toString({
            time: true,
            format: 'locale-numeric',
          })
        }}
      </span>
      <span v-else>---</span>
    </template>

    <template #item.deadline="{ item }">
      <span
        v-if="item.deadline"
        class="font-weight-bold text-deep-orange-darken-3">
        {{
          new CustomDate(item.deadline).toString({
            time: true,
            format: 'locale-numeric',
          })
        }}
      </span>
      <span v-else>---</span>
    </template>

    <template #item.taskStatus="{ item }">
      <v-chip
        v-if="item.statusId"
        :style="getRecommendationTaskStatusChipStyle(item.statusId)"
        border
        class="font-weight-bold"
        density="comfortable"
        rounded="lg"
        size="small"
        variant="flat">
        {{ item.status?.name?.toUpperCase() }}
      </v-chip>
      <span v-else>---</span>
    </template>
  </b-server-table>
</template>

<script lang="ts" setup>
import { useRouter } from 'vue-router'
import { BServerTable } from 'best-modules/components'
import { CustomDate } from 'best-modules/utils'
import { auditRecommendationTaskListHeaders } from '@/utils/tableHeaders'
import { taskList } from '@/requests/task'
import { getPersonName } from 'best-modules/utils/helpers'
import { getColorByStatus } from '@/pages/audit/helpers'

defineOptions({
  name: 'RecommendationList',
})

type RecommendationListItem = {
  id: number
  task: string | null
  executor: string | null
  deadline: string | null
  taskStatusId: number | null
  taskStatus: string | null
}

const RECOMMENDATION_TASK_STATUS_IDS = {
  DONE: 3,
  FINAL_DONE: 5,
} as const

const router = useRouter()

const recommendationTooltipText = [
  'Рекомендації аудиту - тут зібрані всі завдання,',
  'які були поставлені за результатами аудитів.',
  'Натисніть на рядок щоб відкрити завдання.',
].join(' ')

const filterProps = {
  searchPlaceholder: 'Пошук за завданням, виконавцем...',
  filterMap: [{ key: 'search' }],
}

const getRecommendationTaskStatusChipStyle = (statusId: number | null) => {
  switch (statusId) {
    case 2:
      return {
        backgroundColor: '#eaf4ff',
        color: '#1565a9',
      }

    case 3:
    case 5:
      return {
        backgroundColor: '#e9f8f0',
        color: '#187a4d',
      }

    case 4:
      return {
        backgroundColor: '#fff1f1',
        color: '#b42318',
      }

    default:
      return {
        backgroundColor: '#fff8e8',
        color: '#b66f00',
      }
  }
}

const isTaskDone = (statusId: number | null) => {
  return (
    statusId === RECOMMENDATION_TASK_STATUS_IDS.DONE ||
    statusId === RECOMMENDATION_TASK_STATUS_IDS.FINAL_DONE
  )
}

const openTask = (recommendation: RecommendationListItem) => {
  router.push({
    name: 'single-task',
    params: { id: recommendation.id },
  })
}
</script>
